import ConformanceTargetSelect from '@/pages/report/reportComponents/ConformanceTargetSelect';
import GlobalDomainSelect from '@/pages/report/reportComponents/GlobalDomainSelect';
import { Typography } from '@eye-able/ui';
import { cn } from '@/helpers/cn';
import { type ReactNode, useLayoutEffect } from 'react';

interface PageTitleProps {
  description?: ReactNode;
  heading: string;
  icon?: ReactNode;
  showConformanceSelect?: boolean;
  showDomainSelect?: boolean;
  subheading?: string;
}

const PageTitle = ({
  description,
  heading,
  icon,
  showConformanceSelect,
  showDomainSelect,
  subheading,
}: PageTitleProps) => {
  const variant = icon ? 'headline-l' : 'headline-xl';

  useLayoutEffect(() => {
    const section = subheading ? ` - ${subheading}` : '';

    document.title = `${heading}${section} | Eye-Able Dashboard`;
  }, [heading, subheading]);

  return (
    <section className='eye:w-full eye:pb-xs eye:lg:pb-s'>
      <div className='eye:flex eye:items-start eye:gap-xs eye:justify-between eye:max-lg:flex-col-reverse'>
        <div
          className='eye:flex eye:flex-row eye:max-w-full'
          style={{ minWidth: 0 }}
        >
          {icon && <div className='eye:mr-2xs'>{icon}</div>}
          <div>
            <Typography tag='h1' variant={variant}>
              {heading}
            </Typography>
            <Typography
              variant={variant}
              noWrap
              className='eye:font-regular!'
              title={subheading ?? ''}
            >
              {subheading}
            </Typography>
          </div>
        </div>
        <div
          className={cn(
            'eye:flex eye:max-sm:flex-col eye:flex-row eye:gap-xs eye:items-center eye:max-lg:self-end eye:max-sm:w-full',
            variant === 'headline-l' ? 'eye:-mt-12' : 'eye:-mt-8',
          )}
        >
          {showDomainSelect && (
            <GlobalDomainSelect
              {...(showConformanceSelect && {
                slotProps: {
                  popoverContent: {
                    className: 'eye:min-sm:w-[525px] eye:min-sm:mt-5xs',
                  },
                },
              })}
            />
          )}
          {showConformanceSelect && <ConformanceTargetSelect />}
        </div>
      </div>
      {description ? (
        <Typography
          tag='div'
          variant='body-m'
          className={cn('eye:mt-5xs eye:w-full', icon && 'eye:ml-l')}
        >
          {description}
        </Typography>
      ) : null}
    </section>
  );
};

export default PageTitle;
